// Step 2: Plan Selection (After Account Creation)
// Security: Only authenticated users can select plans
// UX: Clear pricing with plan comparison

import React from 'react';
import { SubscriptionTier } from '../../config/subscriptions';
import SubscriptionPlans from '../SubscriptionPlans';

interface PlanSelectionStepProps {
  onPlanSelected: (tier: SubscriptionTier) => void;
  user: any;
  selectedTier: string | undefined;
}

const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({
  onPlanSelected,
  user,
  selectedTier
}) => {
  
  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        
        {/* Header with user welcome */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome, {user?.email?.split('@')[0]}! 
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Your account has been created. Now choose your learning plan.
          </p>
        </div>

        {/* Account Status Indicator */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-green-800 dark:text-green-200 font-medium text-sm">
                ✅ Account Created Successfully
              </p>
              <p className="text-green-700 dark:text-green-300 text-sm">
                Email: {user?.email} • Status: Pending Subscription
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 text-center">
            Choose Your Learning Plan
          </h3>
          
          <SubscriptionPlans
            onSelectPlan={onPlanSelected}
            selectedTier={selectedTier}
            showTitle={false}
          />
        </div>

        {/* Security & Benefits Notice */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="grid md:grid-cols-2 gap-6">
            
            {/* What's Included */}
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Elite Access Includes:
              </h4>
              <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Unlimited access to all interactive math problems
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Personalized learning paths and progress tracking
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Advanced problem-solving tools and calculators
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Priority support and community access
                </li>
              </ul>
            </div>

            {/* Security & Next Steps */}
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure Payment Process:
              </h4>
              <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Your account is already created and secure
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Stripe handles all payment processing safely
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Instant activation after payment confirmation
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                  Cancel anytime with full data retention
                </li>
              </ul>
            </div>

          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">
                ✓
              </div>
              <span className="mr-4">Account Created</span>
            </div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mr-2">
                2
              </div>
              <span className="mr-4 font-medium text-blue-600 dark:text-blue-400">Choose Plan</span>
            </div>
            <div className="flex items-center opacity-50">
              <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-xs font-medium mr-2">
                3
              </div>
              <span>Payment & Activation</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PlanSelectionStep;